import { alpha, Chip, Stack, Typography, useTheme } from '@mui/material';
import { IUnifiedFilter } from 'src/contexts/SearchFilterContext';
import { useStores } from 'src/models';

import { FilterPageConfig } from './SearchFilter';

interface Props {
  currentValues: IUnifiedFilter;
  layout: FilterPageConfig;
}

export const TooltipContents = ({ currentValues, layout }: Props) => {
  const theme = useTheme();
  const rootStore = useStores();
  const { commonCodeStore: codeStore } = rootStore;

  const sectionFilters: {
    section: string;
    filters: { label: string; value: string | string[] }[];
  }[] = [];

  layout.sections.forEach((section) => {
    const filters: { label: string; value: string | string[] }[] = [];

    if (section === 'common') {
      if (currentValues.newStatusCdList?.length) {
        const codes = codeStore.getCodeByName('NEW_STATUS_CD');
        const labels = currentValues.newStatusCdList
          .map((val) => codes.find((code) => String(code.value) === val)?.label)
          .filter((label): label is string => Boolean(label));
        if (labels.length) filters.push({ label: '신규/기저', value: labels ?? '' });
      }

      if (currentValues.ongoingCdList?.length) {
        const codes = codeStore.getCodeByName('ONGOING_CD');
        const labels = currentValues.ongoingCdList
          .map((val) => codes.find((code) => String(code.value) === val)?.label)
          .filter((label): label is string => Boolean(label));
        if (labels.length) filters.push({ label: '스팟/지속', value: labels ?? '' });
      }

      if (!currentValues.noBzStepCd && currentValues.bzStepCdList?.length) {
        const codes = codeStore.getCodeByName('BZ_STEP_CD');
        const labels = currentValues.bzStepCdList
          .map((val) => codes.find((code) => code.value === val)?.label)
          .filter((label): label is string => Boolean(label));
        filters.push({
          label: '영업단계',
          value: labels ?? '',
        });
      }

      if (currentValues.orderPrcnt) {
        const signCode = codeStore
          .getCodeByName('SIGN_CD')
          .find((code) => code.value === Number(currentValues.orderSign));
        filters.push({
          label: '수주확률',
          value: [`${currentValues.orderPrcnt}%`, `${signCode?.label || ''}`],
        });
      }

      if (filters.length > 0) {
        sectionFilters.push({
          section: window.location.pathname === '/project' ? 'Project' : 'Order',
          filters,
        });
      }
    }

    if (section === 'order') {
      if (currentValues.orderNewStatusCdList?.length) {
        const codes = codeStore.getCodeByName('NEW_STATUS_CD');
        const labels = currentValues.orderNewStatusCdList
          .map((val) => codes.find((code) => String(code.value) === val)?.label)
          .filter((label): label is string => Boolean(label));
        if (labels.length) filters.push({ label: '신규/기저', value: labels ?? '' });
      }

      if (currentValues.orderOngoingCdList?.length) {
        const codes = codeStore.getCodeByName('ONGOING_CD');
        const labels = currentValues.orderOngoingCdList
          .map((val) => codes.find((code) => String(code.value) === val)?.label)
          .filter((label): label is string => Boolean(label));
        if (labels.length) filters.push({ label: '스팟/지속', value: labels ?? '' });
      }

      if (!currentValues.noOrderBzStepCd && currentValues.orderBzStepCdList?.length) {
        const codes = codeStore.getCodeByName('BZ_STEP_CD');
        const labels = currentValues.orderBzStepCdList
          .map((val) => codes.find((code) => code.value === val)?.label)
          .filter((label): label is string => Boolean(label));
        filters.push({
          label: '영업단계',
          value: labels ?? '',
        });
      }

      if (currentValues.orderPrcnt) {
        const signCode = codeStore
          .getCodeByName('SIGN_CD')
          .find((code) => Number(code.value) === Number(currentValues.orderSign));
        filters.push({
          label: '수주확률',
          value: [`${currentValues.orderPrcnt}%`, `${signCode?.label || ''}`],
        });
      }

      if (filters.length > 0) {
        sectionFilters.push({
          section: window.location.pathname === '/project' ? 'Project' : 'Order',
          filters,
        });
      }
    }

    if (section === 'sales') {
      if (currentValues.salesNewStatusCdList?.length) {
        const codes = codeStore.getCodeByName('NEW_STATUS_CD');
        const labels = currentValues.salesNewStatusCdList
          .map((val) => codes.find((code) => String(code.value) === val)?.label)
          .filter((label): label is string => Boolean(label));
        if (labels.length) filters.push({ label: '신규/기저', value: labels });
      }

      if (currentValues.salesOngoingCdList?.length) {
        const codes = codeStore.getCodeByName('ONGOING_CD');
        const labels = currentValues.salesOngoingCdList
          .map((val) => codes.find((code) => String(code.value) === val)?.label)
          .filter((label): label is string => Boolean(label));
        if (labels.length) filters.push({ label: '스팟/지속', value: labels });
      }

      if (!currentValues.noSalesBzStepCd && currentValues.salesBzStepCdList?.length) {
        const codes = codeStore.getCodeByName('BZ_STEP_CD');
        const labels = currentValues.salesBzStepCdList
          .map((val) => codes.find((code) => code.value === val)?.label)
          .filter((label): label is string => Boolean(label));
        filters.push({
          label: '영업단계',
          value: labels ?? '',
        });
      }

      if (currentValues.salesPrcnt) {
        const signCode = codeStore
          .getCodeByName('SIGN_CD')
          .find((code) => Number(code.value) === Number(currentValues.salesSign));
        filters.push({
          label: '수주확률',
          // label: '매출확률',
          value: [`${currentValues.salesPrcnt}%`, `${signCode?.label || ''}`],
        });
      }

      if (filters.length > 0) {
        sectionFilters.push({ section: 'Sales', filters });
      }
    }
  });

  if (layout.sections.includes('keyword') && currentValues.text) {
    if (sectionFilters.length > 0) {
      sectionFilters[sectionFilters.length - 1].filters.push({
        label: '키워드',
        value: currentValues.text,
      });
    } else {
      sectionFilters.push({
        section: '',
        filters: [{ label: '키워드', value: currentValues.text }],
      });
    }
  }

  return (
    <Stack p={2} style={{ maxWidth: 350 }}>
      {sectionFilters.length > 0 ? (
        <Stack gap={2}>
          {sectionFilters.map((sectionData, sectionIndex) => (
            <Stack key={sectionIndex}>
              <Typography variant="h6">{sectionData.section}</Typography>

              <Stack spacing={1.5}>
                {sectionData.filters.map((filter, i) => {
                  return (
                    <Stack key={i} gap={0.5}>
                      <Typography variant="body1">{filter.label}</Typography>
                      <Stack gap={0.5} direction={'row'} flexWrap={'wrap'}>
                        {Array.isArray(filter.value) ? (
                          filter.value.map((v, j) => {
                            return (
                              <Chip
                                key={j}
                                label={v}
                                variant="outlined"
                                color={sectionData.section === 'Sales' ? 'secondary' : 'primary'}
                                sx={{
                                  backgroundColor:
                                    sectionData.section === 'Sales'
                                      ? alpha(theme.palette.secondary.light, 0.5)
                                      : alpha(theme.palette.primary.light, 0.5),
                                }}
                              />
                            );
                          })
                        ) : (
                          <Chip
                            label={filter.value}
                            variant="outlined"
                            color={sectionData.section === 'Sales' ? 'secondary' : 'primary'}
                            sx={{
                              backgroundColor:
                                sectionData.section === 'Sales'
                                  ? alpha(theme.palette.secondary.light, 0.5)
                                  : alpha(theme.palette.primary.light, 0.5),
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption">기본 필터 적용</Typography>
      )}
    </Stack>
  );
};
